import { Form } from "@client/content/controls";
import { getRootContainerProps, getRootContainerPropsDep } from "@client/content/lib/helper";
import { ModuleButton, ModuleSecondaryButton } from "@client/content/modules/Button";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { type FormButtonProps } from "@client/controls/Form/component";
import TextArea from "@client/controls/Inputs/TextArea/component";
import { isStaffOrSuperUser } from "@common/appInfo";
import { useI18n } from "@common/hooks/i18n";
import * as React from "react";
import { QuoteComment } from "../../Components";
import { type Forums } from "../../types";

export type MapProps = RootContainerProps & {
  apiInfo: ModuleApiInfo | null;
  canCancel: boolean;
  comment?: Forums.PostComment;
  isBusy: boolean;
  isCommentUpdate: boolean;
  parentComment?: Forums.PostComment;
  resetKey: string;
};

export type ActionProps = {
  onCancel: () => void;
  onSubmit: (data: Forums.View.AddEditPostComment) => void;
};

export type Props = ActionProps & MapProps;
export default React.forwardRef((props: Props, ref: React.RefObject<HTMLDivElement>) => {
  const i18n = useI18n();

  const extraButtons = React.useMemo(
    () =>
      props.canCancel
        ? (innerProps: FormButtonProps) => (
            <div style={{ marginLeft: "auto" }}>
              <ModuleSecondaryButton {...getRootContainerProps(props)} className="cancel_btn" onClick={props.onCancel}>
                {i18n.cancel}
              </ModuleSecondaryButton>
            </div>
          )
        : undefined,
    [props.canCancel, props.onCancel]
  );

  const FormButton = React.useMemo(
    () => (innerProps: FormButtonProps) =>
      (
        <ModuleButton
          {...getRootContainerProps(props)}
          isDisabled={props.isBusy || isStaffOrSuperUser()}
          onClick={innerProps.ifValidated(props.onSubmit)}
        >
          {props.comment ? i18n.updateComment : i18n.submitComment}
        </ModuleButton>
      ),
    [...getRootContainerPropsDep(props), props.isBusy, props.comment, props.onSubmit]
  );

  const fields = React.useMemo(
    () => [
      {
        name: "comment",
        label: i18n.comment,
        Component: TextArea,
        componentOpts: { rows: 6, cols: 10, style: { resize: "none" } },
        isRequired: true,
        defaultValue: props.comment?.comment ?? ""
      }
    ],
    [props.comment]
  );

  return (
    <div ref={ref}>
      {!!props.parentComment && <QuoteComment comment={props.parentComment} isParent={props.isCommentUpdate} />}
      <Form
        {...getRootContainerProps(props)}
        key={props.resetKey}
        className="comment_form"
        fields={fields}
        extraButtons={extraButtons}
        Button={FormButton}
        buttonProps={getRootContainerProps(props)}
        resetOnSubmit
      />
    </div>
  );
});
